import { useEffect, useState } from 'react';

// Components
import { TeamInformationHeader, MatchRow, SeasonStatistics } from '../../components/Statistics';
import TailwindTheme from '../../../../../packages/tailwind/tailwind-theme';

// Hooks
import useMatchById from '../../hooks/useMatchById';
import useRootNodeRef from '../../../../../packages/hooks/useRootNodeRef';
import { useTeamInformation, useTeamStatistics, useMatchByTeamId, useSeasons } from '../../hooks/team';

// Utils
import { ConfigContext } from '@velinsi/smp-widgets-ui';
import { defaultHandleUserInteractionClick, getLabelValue } from '@sportal365/smp-widget-utils';

// Constants
import { USER_INTERACTION } from '../../../../../packages/constants/entityTypes';
import { DROPDOWN_SELECTION_SUBSCRIBE_TYPE } from '../../../../../packages/constants/subscribeTypes';

// Types
import { BaseWidgetProps, ITeamConfig } from '../../../../../packages/types';
import { IThemeCustomization, Themes } from '../../../../../packages/tailwind/types';

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import tailwindStyles from '../../styles.css';

export type TeamWidgetProps = BaseWidgetProps<ITeamConfig>;

export const TeamWidget = ({ themes, config, subscribers, shadow, sdkOptions, cuid }: TeamWidgetProps) => {
  const [season, setSeason] = useState<string | undefined>(config?.dataSeason);
  const { ref } = useRootNodeRef();

  const {
    widgetData: { teamInformation, isLoading: informationLoader },
    handleClick,
  } = useTeamInformation({
    ...config,
    sdkOptions,
    subscribers,
    dataWidgetCuid: cuid,
    dataLabels: config?.dataLabels,
    dataEntityLinks: config?.dataEntityLinks,
  });

  const {
    widgetData: { rows, isLoading: statisticsLoader },
  } = useTeamStatistics({
    ...config,
    sdkOptions,
    subscribers,
    scheme: config?.dataElements?.stats,
    dataLabels: config?.dataLabels,
    dataEntityLinks: config?.dataEntityLinks,
    dataSeason: season,
  });

  const { seasonOptions } = useSeasons({ dataTeam: config?.dataTeam, sdkOptions, dataLabels: config?.dataLabels });

  const {
    widgetData: { match: matchByTeamId, isLoading: matchByTeamIdLoading },
  } = useMatchByTeamId({ ...config, dataWidgetCuid: cuid, sdkOptions, dataOddsMarket: config?.dataOddsMarket });

  const {
    widgetData: { match: matchById, isLoading: matchByIdLoading },
  } = useMatchById({ ...config, dataWidgetCuid: cuid, sdkOptions, dataOddsMarket: config?.dataOddsMarket });

  useEffect(() => {
    if (!season) {
      setSeason(teamInformation?.currentSeason);
    }
  }, [season, teamInformation?.currentSeason]);

  const handleSeasonChange = (value: string) => {
    setSeason(value);
    defaultHandleUserInteractionClick(handleClick, USER_INTERACTION, DROPDOWN_SELECTION_SUBSCRIBE_TYPE);
  };

  if (!teamInformation) return null;

  return (
    <ConfigContext.Provider value={{ ...config, dataAllowInlineOdds: true, sdkOptions }}>
      <TailwindTheme
        isLoading={matchByTeamIdLoading || matchByIdLoading || statisticsLoader || informationLoader}
        shadow={shadow}
        themes={themes ? (themes as IThemeCustomization) : undefined}
        dataTheme={config?.dataTheme ? (config.dataTheme as Themes) : undefined}
        refForShadow={ref}
        tailwindStyles={tailwindStyles}
        bgColor='var(--colors-teamPrimaryBackgroundColor)'
      >
        <TeamInformationHeader
          teamInformation={teamInformation}
          handleClick={handleClick}
          showSocialIcons={config?.dataDisplaySocialIcons ?? true}
          showTeamImage={config?.dataDisplayTeamImage ?? true}
        />
        <MatchRow match={matchById ? matchById : matchByTeamId} showMatch={config?.dataMatchDisplay ?? true} handleClick={handleClick} />
        <SeasonStatistics
          title={getLabelValue(config?.dataLabels?.team_stats, 'Teams stats')}
          rows={rows}
          currentSeason={season}
          seasons={seasonOptions}
          handleSeasonChange={handleSeasonChange}
          showStatistics={config?.dataStatisticsDisplay ?? true}
        />
      </TailwindTheme>
    </ConfigContext.Provider>
  );
};
